import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  listTeams, listGroups, createTeam, updateTeamTimezone,
  listMembers, listTeamMembers, addTeamMember, removeTeamMember,
  type Team, type Group, type Member, type TeamMember,
} from '../lib/api';

// R-353: full, accurate IANA identifier list — no hardcoded data to maintain.
// Supported in all evergreen browsers this console targets.
const TIMEZONE_OPTIONS: string[] = Intl.supportedValuesOf('timeZone').sort();

export function TeamsPage() {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);

  // Management panel
  const [managingTeamId, setManagingTeamId] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<Record<string, TeamMember[]>>({});
  const [orgMembers, setOrgMembers] = useState<Member[]>([]);
  const [panelLoading, setPanelLoading] = useState(false);
  const [addingUserId, setAddingUserId] = useState('');

  // New team form
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [newName, setNewName] = useState('');
  const [newGroupId, setNewGroupId] = useState('');
  const [newTimezone, setNewTimezone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([listTeams(user.orgId), listGroups(user.orgId)])
      .then(([t, g]) => {
        const activeTeams = t.filter((x) => !x.archivedAt);
        const activeGroups = g.filter((x) => !x.archivedAt);
        setTeams(activeTeams);
        setGroups(activeGroups);
        if (activeGroups.length > 0) setNewGroupId(activeGroups[0].id);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user]);

  async function openManage(teamId: string) {
    if (managingTeamId === teamId) {
      setManagingTeamId(null);
      return;
    }
    setManagingTeamId(teamId);
    setAddingUserId('');
    setPanelLoading(true);
    try {
      const [members, allOrgMembers] = await Promise.all([
        teamId in teamMembers
          ? Promise.resolve(teamMembers[teamId])
          : listTeamMembers(user!.orgId, teamId),
        orgMembers.length > 0
          ? Promise.resolve(orgMembers)
          : listMembers(user!.orgId),
      ]);
      setTeamMembers((prev) => ({ ...prev, [teamId]: members }));
      if (orgMembers.length === 0) {
        setOrgMembers(allOrgMembers.filter((m) => m.status === 'active'));
      }
    } finally {
      setPanelLoading(false);
    }
  }

  async function handleAddMember(teamId: string) {
    if (!addingUserId) return;
    await addTeamMember(user!.orgId, teamId, addingUserId);
    const updated = await listTeamMembers(user!.orgId, teamId);
    setTeamMembers((prev) => ({ ...prev, [teamId]: updated }));
    setTeams((prev) => prev.map((t) => t.id === teamId ? { ...t, memberCount: updated.length } : t));
    setAddingUserId('');
  }

  async function handleRemoveMember(teamId: string, userId: string) {
    await removeTeamMember(user!.orgId, teamId, userId);
    const updated = (teamMembers[teamId] ?? []).filter((m) => m.userId !== userId);
    setTeamMembers((prev) => ({ ...prev, [teamId]: updated }));
    setTeams((prev) => prev.map((t) => t.id === teamId ? { ...t, memberCount: updated.length } : t));
  }

  async function handleCreateTeam(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim() || !newGroupId || !newTimezone) return;
    setSubmitting(true);
    try {
      const result = await createTeam(user!.orgId, {
        name: newName.trim(),
        groupId: newGroupId,
        timezone: newTimezone,
      });
      const group = groups.find((g) => g.id === newGroupId);
      setTeams((prev) => [
        ...prev,
        {
          id: result.teamId,
          groupId: newGroupId,
          groupName: group?.name ?? '',
          name: result.name,
          timezone: newTimezone,
          memberCount: 0,
          archivedAt: null,
        },
      ]);
      setShowNewTeam(false);
      setNewName('');
      setNewTimezone('');
    } finally {
      setSubmitting(false);
    }
  }

  function handleTeamTimezoneUpdated(teamId: string, timezone: string) {
    setTeams((prev) => prev.map((t) => (t.id === teamId ? { ...t, timezone } : t)));
  }

  if (!user) return null;

  const groupMap = new Map(groups.map((g) => [g.id, g]));
  const teamsByGroup = groups
    .map((g) => ({ group: g, teams: teams.filter((t) => t.groupId === g.id) }))
    .filter((bg) => bg.teams.length > 0);
  const ungrouped = teams.filter((t) => !groupMap.has(t.groupId));
  const sections = [
    ...teamsByGroup,
    ...(ungrouped.length > 0 ? [{ group: null as Group | null, teams: ungrouped }] : []),
  ];

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Teams</h1>
          <p className="page-subtitle">
            {teams.length} active team{teams.length !== 1 ? 's' : ''} in {user.orgName}
          </p>
        </div>
        <button
          className="btn btn--primary"
          onClick={() => setShowNewTeam((v) => !v)}
        >
          {showNewTeam ? 'Cancel' : '+ New team'}
        </button>
      </div>

      {showNewTeam && (
        <div className="card" style={{ marginBottom: 'var(--space-6)', maxWidth: '480px' }}>
          <h2 style={{ fontSize: '1rem', marginBottom: 'var(--space-4)' }}>Create team</h2>
          <form onSubmit={handleCreateTeam}>
            <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
              <label className="form-label" htmlFor="team-name">Name</label>
              <input
                id="team-name"
                className="form-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Platform, Design…"
                required
                autoFocus
              />
            </div>
            <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
              <label className="form-label" htmlFor="team-group">Group</label>
              <select
                id="team-group"
                className="form-input"
                value={newGroupId}
                onChange={(e) => setNewGroupId(e.target.value)}
                required
              >
                {groups.length === 0 && (
                  <option value="">No groups yet — create one first</option>
                )}
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 'var(--space-5)' }}>
              <label className="form-label" htmlFor="team-timezone">Timezone</label>
              <select
                id="team-timezone"
                className="form-input"
                value={newTimezone}
                onChange={(e) => setNewTimezone(e.target.value)}
                required
              >
                <option value="">Select timezone…</option>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 'var(--space-2)' }}>
                Governs when this team's daily check-in question rolls over. Choose where the team actually works.
              </p>
            </div>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={submitting || !newName.trim() || !newGroupId || !newTimezone}
            >
              {submitting ? 'Creating…' : 'Create team'}
            </button>
          </form>
        </div>
      )}

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}

      {!loading && teams.length === 0 && (
        <div className="empty-state">
          <p className="empty-state__title">No teams yet</p>
          <p className="empty-state__body">
            Teams are the working units where the daily check-in lives. Create a group first, then add teams to it.
          </p>
        </div>
      )}

      {!loading && sections.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
          {sections.map(({ group, teams: sectionTeams }) => (
            <section key={group?.id ?? 'ungrouped'}>
              <h2
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--color-text-muted)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                {group?.name ?? 'Ungrouped'}
              </h2>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Team</th>
                      <th scope="col">Timezone</th>
                      <th scope="col">Members</th>
                      <th scope="col"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionTeams.map((t) => (
                      <Fragment key={t.id}>
                        <tr>
                          <td style={{ fontWeight: 500 }}>{t.name}</td>
                          <td style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                            {t.timezone}
                          </td>
                          <td style={{ color: 'var(--color-text-muted)' }}>{t.memberCount}</td>
                          <td>
                            <button
                              className="btn btn--ghost btn--sm"
                              onClick={() => openManage(t.id)}
                              aria-expanded={managingTeamId === t.id}
                            >
                              {managingTeamId === t.id ? 'Close ▲' : 'Manage ▼'}
                            </button>
                          </td>
                        </tr>
                        {managingTeamId === t.id && (
                          <tr>
                            <td
                              colSpan={4}
                              style={{
                                padding: 'var(--space-4) var(--space-5)',
                                borderTop: 'none',
                                background: 'var(--color-bg)',
                              }}
                            >
                              <MembersPanel
                                orgId={user.orgId}
                                team={t}
                                members={teamMembers[t.id] ?? []}
                                orgMembers={orgMembers}
                                loading={panelLoading}
                                addingUserId={addingUserId}
                                onAddingUserChange={setAddingUserId}
                                onAdd={() => handleAddMember(t.id)}
                                onRemove={(uid) => handleRemoveMember(t.id, uid)}
                                onTimezoneUpdated={(tz) => handleTeamTimezoneUpdated(t.id, tz)}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}

function MembersPanel({
  orgId,
  team,
  members,
  orgMembers,
  loading,
  addingUserId,
  onAddingUserChange,
  onAdd,
  onRemove,
  onTimezoneUpdated,
}: {
  orgId: string;
  team: Team;
  members: TeamMember[];
  orgMembers: Member[];
  loading: boolean;
  addingUserId: string;
  onAddingUserChange: (id: string) => void;
  onAdd: () => void;
  onRemove: (userId: string) => void;
  onTimezoneUpdated: (timezone: string) => void;
}) {
  const memberIds = new Set(members.map((m) => m.userId));
  const addable = orgMembers.filter((m) => !memberIds.has(m.id));

  return (
    <div style={{ maxWidth: '520px' }}>
      <TeamTimezoneEditor
        orgId={orgId}
        teamId={team.id}
        timezone={team.timezone}
        onUpdated={onTimezoneUpdated}
      />

      {loading && <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Loading…</p>}

      {!loading && (
        <>
          <p
            style={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--color-text-muted)',
              marginBottom: 'var(--space-3)',
            }}
          >
            Team members
          </p>

          {members.length === 0 && (
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>
              No members yet.
            </p>
          )}

          {members.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 var(--space-4)' }}>
              {members.map((m) => (
                <li
                  key={m.userId}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'var(--space-2) 0',
                    borderBottom: '1px solid var(--color-border)',
                  }}
                >
                  <span style={{ fontSize: '0.9375rem' }}>
                    {m.displayName}
                    {m.role === 'lead' && (
                      <span
                        style={{
                          marginLeft: 'var(--space-2)',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        Lead
                      </span>
                    )}
                  </span>
                  <button
                    className="btn btn--ghost btn--sm"
                    style={{ color: 'var(--color-danger)' }}
                    onClick={() => onRemove(m.userId)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          {addable.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
              <select
                className="form-input"
                style={{ flex: 1 }}
                value={addingUserId}
                onChange={(e) => onAddingUserChange(e.target.value)}
              >
                <option value="">Select member to add…</option>
                {addable.map((m) => (
                  <option key={m.id} value={m.id}>{m.displayName}</option>
                ))}
              </select>
              <button
                className="btn btn--primary btn--sm"
                disabled={!addingUserId}
                onClick={onAdd}
              >
                Add
              </button>
            </div>
          )}

          {addable.length === 0 && orgMembers.length > 0 && (
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-subtle)' }}>
              All active org members are already on this team.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function TeamTimezoneEditor({
  orgId,
  teamId,
  timezone,
  onUpdated,
}: {
  orgId: string;
  teamId: string;
  timezone: string;
  onUpdated: (timezone: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(timezone);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setSelected(timezone);
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setError(null);
    setEditing(false);
  }

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const res = await updateTeamTimezone(orgId, teamId, selected);
      onUpdated(res.timezone);
      setEditing(false);
    } catch (err) {
      const body = (err as { body?: { error?: string } })?.body;
      setError(
        body?.error === 'invalid_timezone'
          ? 'That timezone is not valid. Please choose another.'
          : 'Failed to update timezone. Try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        marginBottom: 'var(--space-5)',
        paddingBottom: 'var(--space-4)',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <p
        style={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--color-text-muted)',
          marginBottom: 'var(--space-3)',
        }}
      >
        Team timezone
      </p>

      {!editing && (
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <span style={{ fontSize: '0.9375rem' }}>{timezone}</span>
          <button className="btn btn--ghost btn--sm" onClick={startEdit}>
            Edit
          </button>
        </div>
      )}

      {editing && (
        <div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="sr-only" htmlFor={`team-tz-${teamId}`}>Team timezone</label>
            <select
              id={`team-tz-${teamId}`}
              className="form-input"
              style={{ flex: 1, minWidth: '220px' }}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={saving}
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            <button
              className="btn btn--primary btn--sm"
              onClick={handleSave}
              disabled={saving || !selected}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              className="btn btn--ghost btn--sm"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
          {error && (
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-danger)', marginTop: 'var(--space-2)' }}>
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
